import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../Components/ui';
import { CornerDownLeft, Menu, Moon, Sun, Bell, Search } from 'lucide-react';
import { useAuthUser } from 'react-auth-kit';
import User from '../Models/User';
import UserMenu from './UserMenu';
import { useThemeMode } from '../theme/ThemeContext';
import { getHabilitations } from './Sidebar';

/** Normalisation accent-insensible pour la recherche. */
const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

interface MenuEntry { lien: string; label: string; group?: string }

/**
 * Barre supérieure : hamburger (mobile), recherche dans les menus (habilitations),
 * thème, notifications et menu utilisateur.
 */
const Topbar = () => {
    const auth = useAuthUser();
    const user: User = JSON.parse(JSON.stringify(auth() || {}));
    const { mode, toggle } = useThemeMode();
    const navigate = useNavigate();

    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const boxRef = useRef<HTMLDivElement>(null);

    /** Entrées navigables construites depuis les habilitations backend (même source que le Sidebar). */
    const entries = useMemo<MenuEntry[]>(() => {
        const habs = getHabilitations();
        const parents = new Map<string, string>(
            habs.filter((h: any) => h?.CodeHabi?.length === 1).map((h: any) => [h.CodeHabi, h.Libelle])
        );
        return habs
            .filter((h: any) => h?.liens)
            .map((h: any) => ({
                lien: h.liens,
                label: h.Libelle,
                group: h.CodeHabi?.length > 1 ? parents.get(h.CodeHabi[0]) : undefined,
            }));
    }, []);

    const results = useMemo(() => {
        const t = norm(q).trim();
        if (!t) return [];
        return entries
            .filter(e => norm(e.label).includes(t) || norm(e.lien).includes(t))
            .slice(0, 10);
    }, [q, entries]);

    // Fermer la liste au clic extérieur
    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const go = (lien: string) => {
        setOpen(false);
        setQ('');
        navigate(lien);
    };

    const onKey = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') { setOpen(false); return; }
        if (!results.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); go(results[active]?.lien ?? results[0].lien); }
    };

    return (
        <header className="eb-topbar">
            {/* Hamburger — visible uniquement sur mobile (ouvre le sidebar off-canvas) */}
            <button
                className="eb-topbar-iconbtn eb-topbar-burger"
                title="Menu"
                onClick={() => window.dispatchEvent(new CustomEvent('eb:toggle-sidebar'))}
            >
                <Menu />
            </button>

            <div className="eb-topbar-search" ref={boxRef}>
                <Input
                    startIcon={<Search size={18} />}
                    placeholder="Rechercher dans le menu..."
                    type="search"
                    value={q}
                    onChange={(e: any) => { setQ(e.target.value); setActive(0); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKey}
                />
                {open && q.trim() !== '' && (
                    <div className="eb-search-results">
                        {results.length === 0 && (
                            <div className="eb-search-empty">Aucun menu ne correspond à « {q} »</div>
                        )}
                        {results.map((r, i) => (
                            <button
                                key={r.lien + i}
                                type="button"
                                className={`eb-search-item${i === active ? ' active' : ''}`}
                                onMouseEnter={() => setActive(i)}
                                onClick={() => go(r.lien)}
                            >
                                <Search size={14} className="flex-shrink-0 text-[var(--eb-text-muted)]" />
                                <span className="flex-1 min-w-0 text-left">
                                    <span className="block truncate">{r.label}</span>
                                    {r.group && <span className="eb-search-group">{r.group}</span>}
                                </span>
                                <CornerDownLeft size={12} className="flex-shrink-0 text-[var(--eb-text-muted)] opacity-50" />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="eb-topbar-actions">
                <button
                    className="eb-topbar-iconbtn"
                    onClick={toggle}
                    title={mode === 'dark' ? 'Passer en thème clair' : 'Passer en thème sombre'}
                >
                    {mode === 'dark' ? <Sun /> : <Moon />}
                </button>
                <span className="relative inline-flex">
                    <button className="eb-topbar-iconbtn" title="Notifications">
                        <Bell />
                    </button>
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[var(--eb-primary)] pointer-events-none" />
                </span>
                <UserMenu user={user} />
            </div>
        </header>
    );
};

export default Topbar;
